import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import * as clientEnv from 'libs/client-env';
import clsx from 'clsx';
import { txt } from 'libs/i18n';
import { Link } from 'react-router-dom';
import ResponsiveTable, { empiricalScrollHeightAdjust, } from 'components/ResponsiveTable';
import ColumnHeader from 'components/ColumnHeader';
import { isSearchedProject } from 'api/project-api';
import { Tooltip } from 'antd';
import { FilterFilled, LockOutlined, TeamOutlined } from '@ant-design/icons';
import TagList from 'screens/DashboardScreen/components/TagList';
import DateRangeFilter from 'components/DateRangeFilter';
import ParsedSearchResult from '../ParsedSearchResult';
import TagsFilter from '../TagsFilter';
import ProjectStatus from '../ProjectStatus';
import { renderDuration, renderDate } from '../ProjectPanel';
import GlobalSelectionOption from '../GlobalSelectionOption';
import styles from './style.module.less';
import { Row, expandedRowRender, } from './Row';
const ProjectsTable = ({ contentHeight, tableView, selectedProjects, refreshProjects, sortBy, sortOrder, createdDates, updatedDates, projectsSearch, tags, showKeywords, teamTags, tutorialProjectId, tutorialPrepareNext, projectsList, setSelectedProjects, fetchProjects, fetchTags, onGlobalSelection, }) => {
    var _a, _b, _c;
    const [dropdownVisibility, setDropdownVisibility] = useState({
        created: false,
        updated: false,
        tags: false,
    });
    const selectedRowKeys = selectedProjects.map((project) => String(project.id));
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const SCROLL_HEIGHT_ADJUST = empiricalScrollHeightAdjust(((_a = tableView.getViewParams().sieve) === null || _a === void 0 ? void 0 : _a.projectsSearch) === undefined ? 179 : 205);
    const handleTagsFiltered = (tagList) => {
        var _a;
        const oldSieve = (_a = tableView.getViewParams().sieve) !== null && _a !== void 0 ? _a : { filterDates: [] };
        tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { tags: tagList }));
    };
    const handleTagsCanceled = () => {
        var _a;
        const oldSieve = (_a = tableView.getViewParams().sieve) !== null && _a !== void 0 ? _a : { filterDates: [] };
        tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { tags: [] }));
    };
    const handleSort = (columnTitle, sortingOrder) => {
        tableView.setSieve({
            sortBy: columnTitle,
            sortOrder: sortingOrder,
            createdDates,
            updatedDates,
            projectsSearch,
            tags,
            filterDates: [],
        });
    };
    const handleFilterDatesChanged = (filterDates, sortColumn) => {
        var _a;
        const oldSieve = (_a = tableView.getViewParams().sieve) !== null && _a !== void 0 ? _a : { filterDates: [] };
        if (sortColumn === 'created') {
            tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { createdDates: filterDates, sortBy: sortColumn }));
        }
        else {
            tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { updatedDates: filterDates, sortBy: sortColumn }));
        }
    };
    const handleClearDateFilter = (dates) => {
        var _a;
        const oldSieve = (_a = tableView.getViewParams().sieve) !== null && _a !== void 0 ? _a : { filterDates: [] };
        tableView.setSieve(Object.assign(Object.assign({}, oldSieve), { sortBy: 'created', [dates]: [] }));
        refreshProjects(true);
    };
    const columns = [
        {
            title: '',
            key: 'progress',
            align: 'center',
            className: styles.columnProgress,
            width: 50,
            render: (listedProject) => (_jsx(ProjectStatus, { listedProject: listedProject, onRefetch: () => refreshProjects(false) })),
        },
        {
            title: (_jsx(ColumnHeader, { title: "name", columnKey: "name", sortOrder: sortOrder, isSorting: sortBy === 'name', onSort: handleSort })),
            key: 'name',
            align: 'left',
            className: styles.columnProjects,
            render: (listedProject) => {
                var _a, _b;
                // checking if project was created in trial mode
                const isTrialProject = (_b = (_a = listedProject.transcriptionConfig) === null || _a === void 0 ? void 0 : _a.trialTranscription) !== null && _b !== void 0 ? _b : false;
                return (_jsxs(_Fragment, { children: [isTrialProject && (_jsx(Tooltip, { title: txt('trialProjectTooltip'), children: _jsx("span", { className: styles.projectTag, children: "DEMO" }) })), _jsx(Link, { className: clsx({
                                'beey-tutorial-step-open': tutorialProjectId !== 0,
                            }), onClick: tutorialPrepareNext, to: `/edit/${listedProject.id}`, children: isSearchedProject(listedProject)
                                && listedProject.projectNameHighlight.length > 0 ? (_jsx(ParsedSearchResult, { text: listedProject.projectNameHighlight[0], onAction: null })) : (listedProject.description.name || (_jsx("span", { style: { fontStyle: 'italic' }, children: txt('noName') }))) }), listedProject.isReadOnly && (_jsx(Tooltip, { title: txt('readOnlyProject'), children: _jsx("span", { className: clsx(styles.projectTag, styles.projectTagReadOnly), children: _jsx(LockOutlined, {}) }) })), listedProject.isTeamProject && (_jsx(Tooltip, { title: txt('teamProject'), children: _jsx("span", { className: clsx(styles.projectTag, styles.projectTagReadOnly), children: _jsx(TeamOutlined, {}) }) }))] }));
            },
        },
        {
            title: txt('tags'),
            key: 'tags',
            dataIndex: 'Tags',
            align: 'center',
            className: styles.columnTags,
            width: 170,
            render: (__, listedProject) => _jsx(TagList, { tagList: listedProject.tags, teamTags: teamTags }),
            onFilterDropdownOpenChange: (visibility) => setDropdownVisibility({
                created: false,
                updated: false,
                tags: visibility,
            }),
            filterDropdownOpen: dropdownVisibility.tags,
            filterIcon: (_jsx(FilterFilled, { className: clsx({
                    [styles.filterActive]: (tags === null || tags === void 0 ? void 0 : tags.length) !== 0,
                }) })),
            filterDropdown: (_jsx(TagsFilter, { onTagsFiltered: (selectedTags) => handleTagsFiltered(selectedTags), onTagsCanceled: handleTagsCanceled, teamTags: teamTags !== 'loading' ? teamTags.list : [], selectedTags: (_c = (_b = tableView.getViewParams().sieve) === null || _b === void 0 ? void 0 : _b.tags) !== null && _c !== void 0 ? _c : [], onDropdownVisibility: (visibility) => setDropdownVisibility({
                    created: false,
                    updated: false,
                    tags: visibility,
                }) })),
        },
        {
            title: (_jsx(ColumnHeader, { title: "length", columnKey: "length", sortOrder: sortOrder, className: styles.columnCentered, isSorting: sortBy === 'length', onSort: handleSort })),
            key: 'length',
            align: 'center',
            className: styles.columnDuration,
            width: 110,
            render: (listedProject) => renderDuration(listedProject.length),
        },
        {
            title: (_jsx(ColumnHeader, { title: "created", columnKey: "created", className: styles.columnCentered, sortOrder: sortOrder, isSorting: sortBy === 'created', onSort: handleSort })),
            key: 'created',
            align: 'center',
            className: styles.columnCreated,
            width: '10rem',
            onFilterDropdownOpenChange: (visibility) => setDropdownVisibility({
                created: visibility,
                updated: false,
                tags: false,
            }),
            filterDropdownOpen: dropdownVisibility.created,
            filterIcon: (_jsx(FilterFilled, { className: clsx({
                    [styles.filterActive]: (createdDates === null || createdDates === void 0 ? void 0 : createdDates.length) !== 0,
                }) })),
            filterDropdown: (_jsx("div", { className: styles.filterDialog, children: _jsx(DateRangeFilter, { onClearFilter: () => handleClearDateFilter('createdDates'), filterDates: createdDates !== null && createdDates !== void 0 ? createdDates : [], onFilterDatesChanged: (dates) => handleFilterDatesChanged(dates, 'created'), onDropdownVisibility: (visibility) => setDropdownVisibility({
                        created: visibility,
                        updated: false,
                        tags: false,
                    }) }) })),
            render: (listedProject) => renderDate(listedProject.created),
        },
        {
            title: (_jsx(ColumnHeader, { title: "updated", columnKey: "updated", className: styles.columnCentered, sortOrder: sortOrder, isSorting: sortBy === 'updated', onSort: handleSort })),
            key: 'updated',
            align: 'center',
            width: '10rem',
            onFilterDropdownOpenChange: (visibility) => setDropdownVisibility({
                created: false,
                updated: visibility,
                tags: false,
            }),
            filterDropdownOpen: dropdownVisibility.updated,
            filterIcon: (_jsx(FilterFilled, { className: clsx({
                    [styles.filterActive]: (updatedDates === null || updatedDates === void 0 ? void 0 : updatedDates.length) !== 0,
                }) })),
            filterDropdown: (_jsx("div", { className: styles.filterDialog, children: _jsx(DateRangeFilter, { onClearFilter: () => handleClearDateFilter('updatedDates'), filterDates: updatedDates !== null && updatedDates !== void 0 ? updatedDates : [], onFilterDatesChanged: (dates) => handleFilterDatesChanged(dates, 'updated'), onDropdownVisibility: (visibility) => setDropdownVisibility({
                        created: false,
                        updated: visibility,
                        tags: false,
                    }) }) })),
            render: (listedProject) => renderDate(listedProject.updated),
        },
    ];
    const filteredColumns = columns.filter((column) => clientEnv
        .getDisplayedColumns()
        .includes(column.key)
        || column.key === 'name'
        || column.key === 'progress');
    const handleSelectChange = (newSelectedRowKeys) => {
        const selectedIds = new Set(newSelectedRowKeys.map(String));
        const previouslySelected = selectedProjects
            .filter((project) => selectedIds.has(String(project.id)));
        const newlySelected = projectsList === 'loading'
            ? []
            : projectsList.items.filter((project) => selectedIds.has(String(project.id))
                && !previouslySelected.some((selectedProject) => selectedProject.id === project.id));
        setSelectedProjects([...previouslySelected, ...newlySelected]);
    };
    const rowSelection = {
        onChange: handleSelectChange,
        preserveSelectedRowKeys: true,
        selectedRowKeys,
        columnWidth: 30,
        getCheckboxProps: (record) => ({
            disabled: record.deleteAt !== undefined,
        }),
        selections: [
            {
                key: 'all',
                text: (_jsx(GlobalSelectionOption, { totalCount: projectsList === 'loading' ? null : projectsList.totalCount })),
                onSelect: () => onGlobalSelection(true),
            },
            {
                key: 'none',
                text: _jsx("button", { type: "button", children: txt('deselectAll') }),
                onSelect: () => onGlobalSelection(false),
            },
        ],
    };
    const handleRowExpand = (expanded, listedProject) => {
        setExpandedRowKeys(expanded ? [String(listedProject.id)] : []);
    };
    return (_jsx(ResponsiveTable, { tableView: tableView, dataSource: projectsList === 'loading' ? [] : projectsList.items, recordCount: projectsList === 'loading' ? 0 : projectsList.totalCount, components: { body: { row: Row } }, onRow: (listedProject) => {
            var _a, _b;
            return ({
                listedProject,
                search: (_b = (_a = tableView.getViewParams().sieve) === null || _a === void 0 ? void 0 : _a.projectsSearch) !== null && _b !== void 0 ? _b : null,
                showKeywords,
            });
        }, rowClassName: (project) => (project.deleteAt !== undefined ? styles.trashed : ''), rowKey: (listedProject) => String(listedProject.id), onViewChange: () => fetchProjects(true), columns: filteredColumns, expandable: {
            expandedRowRender: (listedProject) => expandedRowRender(listedProject, fetchProjects, fetchTags),
            expandedRowKeys,
            onExpand: handleRowExpand,
        }, scroll: { x: 900, y: contentHeight - SCROLL_HEIGHT_ADJUST }, locale: {
            filterConfirm: txt('filter'),
            triggerDesc: txt('triggerDesc'),
            triggerAsc: txt('triggerAsc'),
            cancelSort: txt('cancelSort'),
        }, rowSelection: rowSelection, loading: projectsList === 'loading', showSizeChanger: false }));
};
export default ProjectsTable;
